import { createRouter, createWebHistory } from 'vue-router'

import { Role } from '@/enums/roles.ts'
import { isAuthenticated } from '@/libs/auth'
import { hasRoles } from '@/libs/roles.ts'
import { useAppStore } from '@/stores/app'

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) return savedPosition
        else if (to.hash) return { selector: to.hash }
        else if (from.path === to.path) return {}

        return {
            top: 1,
            behavior: 'smooth'
        }
    },
    routes: [
        {
            path: '/',
            name: 'dashboard',
            component: () => import('@/views/Dashboard.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/users',
            name: 'users',
            component: () => import('@/views/user/Users.vue'),
            meta: {
                requiresAuth: true,
                requiredRoles: [Role.ADMIN]
            }
        },
        {
            path: '/additional-equipments',
            name: 'additional-equipments',
            component: () => import('@/views/additionalEquipment/AdditionalEquipments.vue'),
            meta: {
                requiresAuth: true,
                requiredRoles: [Role.ADMIN]
            }
        },
        {
            path: '/card-payment-equipments',
            name: 'card-payment-equipments',
            component: () => import('@/views/cardPaymentEquipment/CardPaymentEquipments.vue'),
            meta: {
                requiresAuth: true,
                requiredRoles: [Role.ADMIN]
            }
        },
        {
            path: '/current-programs',
            name: 'current-programs',
            component: () => import('@/views/currentProgram/CurrentPrograms.vue'),
            meta: {
                requiresAuth: true,
                requiredRoles: [Role.ADMIN]
            }
        },
        {
            path: '/customers',
            name: 'customers',
            component: () => import('@/views/customer/Customers.vue'),
            meta: {
                requiresAuth: true,
                requiredRoles: [Role.ADMIN, Role.USER]
            }
        },
        {
            path: '/customer/:id',
            name: 'customer',
            component: () => import('@/views/customer/Customer.vue'),
            meta: {
                requiresAuth: true,
                requiredRoles: [Role.ADMIN, Role.USER]
            }
        },
        {
            path: '/equipment-procurements',
            name: 'equipment-procurements',
            component: () => import('@/views/equipmentProcurement/EquipmentProcurements.vue'),
            meta: {
                requiresAuth: true,
                requiredRoles: [Role.ADMIN]
            }
        },
        {
            path: '/customers-without-added-users',
            name: 'customers-without-added-users',
            component: () => import('@/views/customer/CustomersWithoutAddedUsers.vue'),
            meta: {
                requiresAuth: true,
                requiredRoles: [Role.ADMIN, Role.USER]
            }
        },
        {
            path: '/calendar',
            name: 'calendar',
            component: () => import('@/views/calendar/Calendar.vue'),
            meta: {
                requiresAuth: true,
                requiredRoles: [Role.ADMIN, Role.USER]
            }
        },
        {
            path: '/profile',
            name: 'profile',
            component: () => import('@/views/profile/Profile.vue'),
            meta: {
                requiresAuth: true
            }
        },
        {
            path: '/login',
            name: 'login',
            component: () => import('@/views/Login.vue'),
            meta: {
                layout: 'Blank',
                requiresAuth: false
            }
        },
        {
            path: '/:pathMatch(.*)*',
            redirect: '/'
        }
    ]
})

router.beforeEach((to, from, next) => {
    const loggedIn: boolean = isAuthenticated()
    const appStore = useAppStore()

    if (loggedIn && to.name === 'login') {
        next({ name: 'dashboard' })
        return
    }

    if (to.meta.requiresAuth && !isAuthenticated()) {
        appStore.setLogout()
        next({ name: 'login' })
    }

    if (to.meta.requiredRoles && isAuthenticated()) {
        if (!hasRoles(...to.meta.requiredRoles)) {
            next({ name: 'dashboard' })
        }
    }

    next()
})

export default router
